import React from 'react'
import PropTypes from 'prop-types'
import ReactGA from 'react-ga'
import { Redirect } from 'react-router'

import { Formik, Form, Field } from 'formik'
import * as Yup from 'yup'

import { useAuth } from '../../AuthProvider/AuthProvider'
import { useRestaurant } from '../../RestaurantProvider/RestaurantProvider'
import { dataByTypename } from '../../../utils/apollo-helpers'

import { PoweredByToastModal } from '../../PoweredByToastModal/PoweredByToastModal'
import { Notification } from '../../Notification/Notification'
import Input from '../../Form/Input/Input'
import Button from '../../Button/Button'

import VerifyLock from '../../../assets/verify-lock.svg'

import './MFAVerificationModal.css'

export const MFA_TYPE_MAP = {
  'EMAIL_CHALLENGE': 'email',
  'SMS_CHALLENGE': 'phone'
}

export const MFAVerificationModal = ({
  onClose = () => {}
}) => {
  const {
    mfaLogin,
    loading,
    error,
    setMfaState,
    challengeToken,
    mfaType,
    email,
    maskedRecipient
  } = useAuth()
  const { getRestaurantPath } = useRestaurant()

  const handleClose = () => {
    setMfaState({
      mfaRequiresVerification: false,
      challengeToken: '',
      mfaType: '',
      maskedRecipient: ''
    })

    onClose()
  }

  if (!challengeToken) {
    return <Redirect to={getRestaurantPath()} />
  }

  let recipient = `We sent a code to your ${MFA_TYPE_MAP[mfaType]}`

  if (maskedRecipient) {
    recipient = recipient + ` ${maskedRecipient}`
  }

  recipient = recipient + '.'

  return (
    <div styleName='mfaVerificationModal'>
      <PoweredByToastModal
        onClose={handleClose}
      >
        <div styleName='headerWrapper'>
          <VerifyLock />
          <h2 data-testid='mfa-verification-modal-header'>Verify Your Account</h2>
          <div data-testid='mfa-verification-modal-description' styleName='description'>
            <p>{ recipient }</p>
            <p>Please enter it below to confirm your identity.</p>
          </div>
        </div>
        <Formik
          initialValues={{ code: '' }}
          validationSchema={Yup.object().shape({
            code: Yup.string()
              .trim()
              .required('Verification code is required')
          })}
          onSubmit={async values => {
            const input = { variables: { input: {
              code: values.code.toString().trim(),
              challengeToken,
              email
            } } }
            const result = await mfaLogin(input)
            const {
              AuthenticationResponse,
              MfaChallengeResponse
            } = dataByTypename(result.data.mfaLogin)

            if (AuthenticationResponse) {
              ReactGA.event({
                category: 'User Account',
                action: 'Login Success'
              })
              setMfaState({
                mfaRequiresVerification: false
              })
              handleClose()
            }

            if (MfaChallengeResponse) {
              setMfaState({
                mfaRequiresVerification: true,
                email,
                ...result.data.mfaLogin
              })
            }
          }}
        >
          {({ isValid }) => (
            <Form>
              {error && !loading && (
                <Notification severity='error'>
                  Invalid code.
                </Notification>
              )}
              <Field
                data-testid='mfa-modal-code'
                autoFocus
                id='mfa-code'
                name='code'
                type='text'
                inputMode='numeric'
                label='Verification Code'
                component={Input}
                instructions={'*Verification code must be 6 characters'}
              />
              <Button
                data-testid='mfa-verify-modal-submit'
                type='submit'
                style='primary'
                responsive
                loading={loading}
                disabled={!isValid}
              >
                Log In
              </Button>
            </Form>
          )}
        </Formik>
      </PoweredByToastModal>
    </div>
  )
}

MFAVerificationModal.propTypes = {
  onClose: PropTypes.func.isRequired
}
